import { type ReactNode } from 'react';
import * as S from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/utils/cn';

type Option = { value: string; label: string };
type Props = {
  options: Option[];
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
};

const ALL = '__all__';
const TRIGGER = cn(
  'inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface',
  'px-2.5 py-1.5 text-xs outline-none transition-colors',
  'hover:border-text-tertiary/30 focus:ring-1 focus:ring-brand-red/30'
);
const CONTENT = cn(
  'z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg',
  'border border-border-subtle bg-surface-elevated p-1 shadow-md'
);
const ITEM_CLS = cn(
  'relative flex cursor-pointer items-center rounded-sm py-1.5 pl-6 pr-3 text-xs outline-none',
  'text-text-secondary transition-colors',
  'data-[highlighted]:bg-white/[0.06] data-[highlighted]:text-text-primary',
  'data-[state=checked]:text-brand-red'
);

function Item({ option }: { option: Option }): ReactNode {
  return (
    <S.Item value={option.value} className={ITEM_CLS}>
      <S.ItemIndicator className="absolute left-1.5">
        <Check size={11} />
      </S.ItemIndicator>
      <S.ItemText>{option.label}</S.ItemText>
    </S.Item>
  );
}

function resolveLabel(
  value: string,
  placeholder: string,
  options: Option[]
): string {
  if (!value || value === ALL) return placeholder;
  return options.find((o) => o.value === value)?.label ?? placeholder;
}

function Trigger({
  value,
  placeholder,
  options,
}: Omit<Props, 'onChange'>): ReactNode {
  const active = !!value && value !== ALL;
  return (
    <S.Trigger
      className={cn(
        TRIGGER,
        active ? 'text-text-primary' : 'text-text-tertiary'
      )}
    >
      <span>{resolveLabel(value, placeholder, options)}</span>
      <S.Icon>
        <ChevronDown size={11} className="text-text-tertiary" />
      </S.Icon>
    </S.Trigger>
  );
}

function Menu({ options }: Pick<Props, 'options'>): ReactNode {
  return (
    <S.Portal>
      <S.Content position="popper" sideOffset={4} className={CONTENT}>
        <S.Viewport>
          {options.map((o) => (
            <Item key={o.value} option={o} />
          ))}
        </S.Viewport>
      </S.Content>
    </S.Portal>
  );
}

export function DropdownSelect({
  options,
  value,
  placeholder,
  onChange,
}: Props): ReactNode {
  return (
    <S.Root value={value} onValueChange={onChange}>
      <Trigger value={value} placeholder={placeholder} options={options} />
      <Menu options={options} />
    </S.Root>
  );
}
